import Tribute from 'tributejs';

document.addEventListener('alpine:init', () => {
  Alpine.data('chipTextarea', ({ state, categories, colorMap, disabled, placeholder }) => ({
    tokenOpen: '[',
    tokenClose: ']',
    state: state,
    categories: categories,
    colorMap: colorMap,
    disabled: disabled,
    placeholder: placeholder || '',
    isFocused: false,
    isEmpty: true,
    syncing: false,
    tribute: null,

    init() {
      this.$nextTick(() => {
        this.renderFromState();
        this.checkEmpty();
        this.bindChipRemoveButtons();
        if (!this.disabled) {
          this.initTribute();
        }
      });

      this.$watch('state', () => {
        if (this.syncing) return;
        this.renderFromState();
        this.checkEmpty();
        this.$nextTick(() => this.bindChipRemoveButtons());
      });
    },

    initTribute() {
      const values = this.categories.flatMap((cat) =>
        cat.records.map((rec) => ({
          key: rec.key,
          label: rec.label,
          categoryKey: cat.key,
          categoryLabel: cat.label,
          color: cat.color,
        }))
      );

      this.tribute = new Tribute({
        trigger: '@',
        values,
        lookup: 'label',
        fillAttr: 'label',
        requireLeadingSpace: false,
        allowSpaces: true,
        menuItemLimit: 25,
        containerClass: 'tribute-container',
        selectClass: 'tribute-selected',
        menuItemTemplate: (item) => {
          const colors = this.colorMap[item.original.color] || this.colorMap['gray'];
          return `<span class="tribute-item-label">${item.string}</span>
                  <span class="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}">${this.escapeHtml(item.original.categoryLabel)}</span>`;
        },
        selectTemplate: (item) => {
          return this.buildChipHtml(item.original.categoryKey, item.original.key) + '\u200B';
        },
        noMatchTemplate: () => '<li class="tribute-no-match">No results</li>',
      });

      this.tribute.attach(this.$refs.editor);

      this.$refs.editor.addEventListener('tribute-replaced', () => {
        this.syncState();
        this.$nextTick(() => this.bindChipRemoveButtons());
      });
    },

    renderFromState() {
      const editor = this.$refs.editor;
      if (!editor) return;

      const val = this.state || '';
      editor.innerHTML = this.tokensToHtml(val);
      this.checkEmpty();
    },

    tokensToHtml(text) {
      if (!text) return '';

      const tokenRegex = /\[(\w+):([^\]]+)\]/g;
      let result = '';
      let lastIndex = 0;

      let match;
      while ((match = tokenRegex.exec(text)) !== null) {
        const before = text.slice(lastIndex, match.index);
        if (before) result += this.escapeHtml(before);

        const catKey = match[1];
        const recKey = match[2];
        result += this.buildChipHtml(catKey, recKey);
        result += '\u200B';

        lastIndex = match.index + match[0].length;
      }

      const remaining = text.slice(lastIndex);
      if (remaining) result += this.escapeHtml(remaining);

      return result;
    },

    buildChipHtml(catKey, recKey) {
      const cat = this.categories.find((c) => c.key === catKey);
      const rec = cat?.records.find((r) => r.key === recKey);
      const label = rec?.label || `${catKey}:${recKey}`;
      const color = cat?.color || 'gray';
      const colors = this.colorMap[color] || this.colorMap['gray'];

      return `<span contenteditable="false" data-chip-category="${this.escapeAttr(catKey)}" data-chip-record="${this.escapeAttr(recKey)}" class="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium align-baseline cursor-default select-none ${colors.bg} ${colors.text} ${colors.border}">${this.escapeHtml(label)}<button type="button" data-chip-remove tabindex="-1" class="ml-0.5 inline-flex items-center rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10" aria-label="Remove">&times;</button></span>`;
    },

    serializeToTokens() {
      const editor = this.$refs.editor;
      if (!editor) return '';

      let text = '';
      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent.replace(/\u200B/g, '');
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.dataset.chipCategory && node.dataset.chipRecord) {
            text += this.tokenOpen + node.dataset.chipCategory + ':' + node.dataset.chipRecord + this.tokenClose;
          } else if (node.tagName === 'BR') {
            text += '\n';
          } else {
            for (const child of node.childNodes) {
              walk(child);
            }
            if (node.tagName === 'DIV' && node !== editor && node.nextSibling) {
              text += '\n';
            }
          }
        }
      };
      walk(editor);
      return text;
    },

    syncState() {
      this.syncing = true;
      this.state = this.serializeToTokens();
      this.checkEmpty();
      this.$nextTick(() => {
        this.syncing = false;
      });
    },

    checkEmpty() {
      const editor = this.$refs.editor;
      if (!editor) return;
      const text = editor.textContent?.replace(/\u200B/g, '').trim() || '';
      const hasChips = editor.querySelector('[data-chip-category]') !== null;
      this.isEmpty = text.length === 0 && !hasChips;
    },

    onInput() {
      this.syncState();
    },

    onKeydown(e) {
      if (this.disabled) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        if (range.collapsed) {
          const node = range.startContainer;
          const offset = range.startOffset;

          if (e.key === 'Backspace') {
            let prev = null;
            if (node.nodeType === Node.TEXT_NODE && offset === 0) {
              prev = node.previousSibling;
            } else if (node.nodeType === Node.ELEMENT_NODE && offset > 0) {
              prev = node.childNodes[offset - 1];
            }
            if (prev?.nodeType === Node.TEXT_NODE && prev.textContent === '\u200B') {
              prev = prev.previousSibling;
            }
            if (prev?.dataset?.chipCategory) {
              e.preventDefault();
              if (prev.nextSibling?.textContent === '\u200B') prev.nextSibling.remove();
              prev.remove();
              this.syncState();
              return;
            }
          }

          if (e.key === 'Delete') {
            let next = null;
            if (node.nodeType === Node.TEXT_NODE && offset === node.textContent.length) {
              next = node.nextSibling;
            } else if (node.nodeType === Node.ELEMENT_NODE && offset < node.childNodes.length) {
              next = node.childNodes[offset];
            }
            if (next?.nodeType === Node.TEXT_NODE && next.textContent === '\u200B') {
              next = next.nextSibling;
            }
            if (next?.dataset?.chipCategory) {
              e.preventDefault();
              if (next.previousSibling?.textContent === '\u200B') next.previousSibling.remove();
              next.remove();
              this.syncState();
              return;
            }
          }
        }
      }
    },

    onPaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      // Insert plain text at cursor position
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      this.syncState();
    },

    onBlur() {
      setTimeout(() => {
        this.isFocused = false;
      }, 150);
    },

    bindChipRemoveButtons() {
      const editor = this.$refs.editor;
      if (!editor) return;
      editor.querySelectorAll('[data-chip-remove]').forEach((btn) => {
        btn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const chip = btn.closest('[data-chip-category]');
          if (chip) {
            if (chip.nextSibling?.textContent === '\u200B') chip.nextSibling.remove();
            chip.remove();
            this.syncState();
          }
        };
      });
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },

    escapeAttr(str) {
      return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },
  }));
});
